/** @format */

import React, {useMemo, useState} from 'react';

import {Collapse} from '@mui/material';
import {SelectChangeEvent} from '@mui/material/Select';
import UnfoldMore from '@mui/icons-material/UnfoldMore';
import UnfoldLess from '@mui/icons-material/UnfoldLess';

import {TableVirtualized} from 'components/Table';
import SingleSelect from 'components/InputsNew/SingleSelect';
import type {OptionInterface} from 'components/InputsNew/SingleSelect/types';

import {TanStackTableData} from 'components/Table/core/types';

import {
  ExpandableSection,
  ExpandButton,
  ExpandableContent,
  TablesGrid,
  TableSection,
  TableTitle,
  ExpandableHeaderContainer,
  SelectContainer,
} from './styles';
import {ExpandableTableProps} from '../../types';
import {CAMPAIGNS_COLUMNS, METRIC_GOALS_COLUMNS} from './constants';
import {mapCampaignsToTableData} from './helpers/mapCampaignsToTableData';
import {mapMetricGoalsToTableData} from './helpers/mapMetricGoalsToTableData';

const ExpandableTables = ({
  campaigns,
  metricGoals,
  onCampaignFilterChange,
  selectedCampaignId = '',
}: ExpandableTableProps) => {
  const [isTablesExpanded, setIsTablesExpanded] = useState(false);

  const campaignsTableData = useMemo(() => mapCampaignsToTableData(campaigns), [campaigns]);
  const metricGoalsTableData = useMemo(() => mapMetricGoalsToTableData(metricGoals), [metricGoals]);

  const campaignOptions = useMemo((): OptionInterface[] => {
    return [
      {value: '', text: 'Все'},
      ...campaigns.map(campaign => ({
        value: campaign.id,
        text: campaign.name,
      })),
    ];
  }, [campaigns]);

  const handleCampaignChange = (event: SelectChangeEvent<unknown>) => {
    onCampaignFilterChange(event.target.value as string);
  };

  const calcTableHeight = (items: TanStackTableData[]) => {
    const ROW_HEIGHT = 53;
    const HEADER_HEIGHT = 72;
    return HEADER_HEIGHT + items.length * ROW_HEIGHT + 20;
  };

  return (
    <ExpandableSection>
      <ExpandableHeaderContainer>
        <SelectContainer>
          <SingleSelect
            value={selectedCampaignId}
            label='Кампания'
            onChange={handleCampaignChange}
            options={campaignOptions}
            placeholder='Все кампании'
          />
        </SelectContainer>

        <ExpandButton
          onClick={() => setIsTablesExpanded(!isTablesExpanded)}
          startIcon={isTablesExpanded ? <UnfoldLess /> : <UnfoldMore />}
        >
          Показать все параметры отчёта
        </ExpandButton>
      </ExpandableHeaderContainer>

      <Collapse in={isTablesExpanded}>
        <ExpandableContent>
          <TablesGrid>
            <TableSection sx={{width: 500}}>
              <TableTitle>Кампании ({campaigns.length})</TableTitle>
              <TableVirtualized
                columns={CAMPAIGNS_COLUMNS}
                data={campaignsTableData}
                tableHeight={calcTableHeight(campaignsTableData)}
                enableSorting={false}
              />
            </TableSection>

            <TableSection sx={{width: 700}}>
              <TableTitle>Цели из Метрики ({metricGoals.length})</TableTitle>
              <TableVirtualized
                columns={METRIC_GOALS_COLUMNS}
                data={metricGoalsTableData}
                tableHeight={calcTableHeight(metricGoalsTableData)}
                enableSorting={false}
              />
            </TableSection>
          </TablesGrid>
        </ExpandableContent>
      </Collapse>
    </ExpandableSection>
  );
};

export default ExpandableTables;
